import {aggregateUseCase} from "../../../../usecases/object";
import { parseDateRangeToQuery } from "../../helper/dateHelper";
import { buildBiometricLogsPipeline } from "./BiometricLogsPipeline";
import { fetchLookupData } from "./AttendanceLookupData";
import { buildLogLookupMap, shouldSkipDate, getNextLog, parseTimeToMinutes, createEmployeeStats, getDateRangeIndex, isDateInTaggingRanges, isMonday, getSuspensionOverlapMins, hasFullDaySuspension, isGraveyardSchedule, isLateAfter7AM, preprocessLogs } from "./utils/AttendanceUtils";
import { _isAbsent, _isHalfDay, getAPBracketedMinutes } from "./utils/AttendanceRules";
import { getScheduleForDay } from "./utils/ScheduleUtils";
import { _toISOString } from "../../helper/dateHelper";
import { _chunkArray } from "../helper-payroll/HelperFunction";

export const processAttendanceStats = async (objects, datePicked, caseName, airportSched) => {

    const userIDs = [];
    const employeeIdMap = new Map();

     const isArrayDate = Array.isArray(datePicked);
    const dateRanges = isArrayDate ? datePicked : [datePicked];
    console.log("dateRanges", dateRanges);
    
    for (let i = 0; i < objects.length; i++) {
      const employee = objects[i];
      const userIDRaw = employee?.biometricId ?? employee?.id;
      if (userIDRaw) {
        const userID = String(userIDRaw);
        userIDs.push(userID);
        const employeeId = employee?.id;
        if (employeeId) {
          employeeIdMap.set(userID, employeeId);
        }
      }
    }

    const parsedDateRanges = dateRanges.map((range) => {
        return parseDateRangeToQuery(range);
    });

    const BATCH_SIZE = 200;
    const userIDBatches = _chunkArray(userIDs, BATCH_SIZE);
    
    const batchPromises = userIDBatches.map((batch) => {
      const pipeline = buildBiometricLogsPipeline(parsedDateRanges, batch, employeeIdMap);
      return aggregateUseCase().execute("biometric_logs", pipeline);
    });

    const [batchResults, lookupData] = await Promise.all([
        Promise.all(batchPromises).then((results) => results.flat()),
        fetchLookupData(objects, parsedDateRanges, isArrayDate, airportSched),
      ]);

    const data = batchResults;
    console.log("data", data);

    const processedLogs =   await applyLateUndertimeStats(data, lookupData, parsedDateRanges, isArrayDate, caseName, employeeIdMap);
    console.log("processedLog", processedLogs);

  return {processedLogs, lookupData};
};

const applyLateUndertimeStats = async (logs, lookupData, parsedDateRanges = [], isArrayDate = false, caseName, employeeIdMap) => {
    const dateRangeCount = parsedDateRanges.length || 1;
    const employeeLateUTMins = {};
    const logLookupMap = buildLogLookupMap(logs);
    const preprocessedLogs = await preprocessLogs(logs, lookupData, logLookupMap, employeeIdMap, parsedDateRanges);

    const preprocessedLogByUserDate = new Map();
    for (let j = 0; j < preprocessedLogs.length; j++) {
      const pl = preprocessedLogs[j];
      if (!pl?.date || pl.userID == null) continue;
      const key = `${String(pl.userID)}:${pl.date}`;
      if (!preprocessedLogByUserDate.has(key)) {
        preprocessedLogByUserDate.set(key, pl);
      }
    }

    console.log("preprocessedLogs", preprocessedLogs);

    const isAirportDate = (userID, dateStr) => {
      if (!dateStr) return false;
      const airportDates = lookupData[String(userID)]?.airportDates;
      if (!airportDates || !(airportDates instanceof Set)) return false;
      return airportDates.has(dateStr);
    };

    const logDatesByUser = {};
    const nonWorkingOverrideDatesByUser = {};

    // const cases = "BASIC_BACKPAY";

  const basicPayroll= [
    "BASIC_ADDENDUM",
    "BASIC_BACKPAY",
    "BASIC_JO_SALARY",
    "BASIC_JO_ADDENDUM",
    "INDIVIDUAL_INITIAL_LAST",
  ].includes(caseName);

  const notJOPayroll = [
    "BASIC_ADDENDUM",
    "BASIC_BACKPAY",
    "INDIVIDUAL_INITIAL_LAST",
  ].includes(caseName);

  const absentRules = {
    isBasic: notJOPayroll,
    isAP: ["AP_GENERAL_NEWLY", "AP_BACKPAY", "INDIVIDUAL_INITIAL_LAST_AP"].includes(caseName),
    isJO: ["BASIC_JO_SALARY", "BASIC_JO_ADDENDUM", "INDIVIDUAL_JO_INITIAL_LAST"].includes(caseName),
  };

  const isIndividualPayroll = ["INDIVIDUAL_INITIAL_LAST", "INDIVIDUAL_INITIAL_LAST_AP", "INDIVIDUAL_JO_INITIAL_LAST"].includes(caseName);

  const isAugmentationPayroll = ["AP_GENERAL_NEWLY", "AP_BACKPAY"].includes(caseName);

  const payrollWithHalfDay = [
    "BASIC_ADDENDUM",
    "BASIC_BACKPAY",
    "INDIVIDUAL_INITIAL_LAST"
  ].includes(caseName);
    
    for (let i = 0; i < preprocessedLogs.length; i++) {
      const log = preprocessedLogs[i];

      if (log.isVirtual || !isDateInTaggingRanges(log.date, parsedDateRanges)) {
        continue;
      }

      if (!log.effectiveSchedule) {
          continue;
      }

      const isTwentyFourSeven = ["12", "9"].some((prefix) =>
        log?.twentyFourSeven?.startsWith(prefix)
      );

      const stampDate = log.stampDate ? new Date(log.stampDate) : null;
      // const isTwentyFourSeven = log.twentyFourSeven || lookupData[log.userID]?.isTwentyFourSeven || false;
      
      const twentyFourSeven = log.twentyFourSeven || lookupData[log.userID]?.twentyFourSeven;
      const holidaySet = lookupData[log.userID]?.holidays;
      const suspensionSet = lookupData[log.userID]?.suspensions;
      const suspensionRangesByDate = lookupData[log.userID]?.suspensionRangesByDate || new Map();
      // console.log("suspensionSet", suspensionSet);
      const manualDtrSet = lookupData[log.userID]?.manualDtr;
      const obSet = lookupData[log.userID]?.ob;
      const timeoffSet = lookupData[log.userID]?.timeoff;
      const leaveWithPaySet = lookupData[log.userID]?.leaveWithPay;
      const mandatoryLeaveSet = lookupData[log.userID]?.mandatoryLeave;
      const leaveWithoutPaySet = lookupData[log.userID]?.leaveWithoutPay;

      if (absentRules.isAP && log.timeRecStats.includes("SLIDE")) {
        continue;
      }
      
      const scheduleToUse = log.scheduleUpdate || log?.effectiveSchedule || lookupData[log.userID]?.schedule;
      let scheduleForDay = getScheduleForDay(scheduleToUse, stampDate);
      // console.log("scheduleForDays", scheduleForDay);

      if (!scheduleForDay) {
        if (log.date && log.scheduleUpdate) {
          if (!nonWorkingOverrideDatesByUser[log.userID]) {
            nonWorkingOverrideDatesByUser[log.userID] = new Set();
          }
          nonWorkingOverrideDatesByUser[log.userID].add(log.date);
        }
        continue;
      }


      const isGraveyard = isGraveyardSchedule(scheduleForDay, scheduleToUse);

       let nextLog = null;
      
      if(isGraveyard){
          nextLog = getNextLog(log, logLookupMap);
      }

      if (log.maxDate) {
        const currentDate = new Date(`${log.date}T00:00:00.000Z`);
        const prevDate = new Date(currentDate);
        prevDate.setDate(prevDate.getDate() - 1);
        const prevDateStr = _toISOString(prevDate);
        const prevLogKey = `${String(log.userID)}:${prevDateStr}`;
        const prevLog = preprocessedLogByUserDate.get(prevLogKey);
        const prevMonthKey = prevDateStr?.slice(0, 7);
        const logMonthKey = log.date?.slice(0, 7);

        const crossesMonthBoundary =
          Boolean(prevMonthKey && logMonthKey) && prevMonthKey !== logMonthKey;

        if (crossesMonthBoundary && !prevLog?.isGraveyard) {
          continue;
        }
      }


      if (log.date) {
        if (!logDatesByUser[log.userID]) {
          logDatesByUser[log.userID] = new Set();
        }
        logDatesByUser[log.userID].add(log.date);
      }

      // Skip processing logs on holidays (non-24/7 only), manual DTR, OB, or timeoff (all employees)
      if ((!isIndividualPayroll && shouldSkipDate(log.date, { dateSet: holidaySet, isTwentyFourSeven, check24Seven: true })) ||
          // shouldSkipDate(log.date, { dateSet: leaveWithPaySet }) ||
          shouldSkipDate(log.date, { dateSet: leaveWithoutPaySet }) ||
          shouldSkipDate(log.date, { dateSet: manualDtrSet }) ||
          shouldSkipDate(log.date, { dateSet: obSet }) ||
          shouldSkipDate(log.date, { dateSet: timeoffSet })) {
        continue; 
      }

      const skipLeave = absentRules.isAP
        ? mandatoryLeaveSet?.has(log.date) ?? false
        : leaveWithPaySet?.has(log.date) ?? false;
      
      if (skipLeave) {
        continue;
      }

      const useLateTime = isGraveyard ? basicPayroll : notJOPayroll;


      const schedTimeIn = useLateTime 
        ? scheduleForDay?.lateTimeIn || scheduleForDay?.timein 
        : scheduleForDay?.timein;
      const schedTimeOut = useLateTime 
        ? scheduleForDay?.earliestTimeOut || scheduleForDay?.timeout 
        : scheduleForDay?.timeout;
      const schedInMins = parseTimeToMinutes(schedTimeIn);
      const schedOutMins = parseTimeToMinutes(schedTimeOut);

      // skip log if suspension spans whole day for non-24/7 or regular employee
      if (!isIndividualPayroll && !isTwentyFourSeven && suspensionSet.has(log.date)) {
        const isFullDaySuspension = hasFullDaySuspension(suspensionRangesByDate, log.date, schedInMins, schedOutMins);
        if (isFullDaySuspension) {
          continue;
        }
      }

      const actualTimeInStr = isGraveyard
        ? log.timePmIn && log.timePmIn !== "--:--"
          ? log.timePmIn
          : log.timeIn
        : log.timeIn && log.timeIn !== "--:--"
          ? log.timeIn
          : log.timePmIn;
        
      const actualTimeOutStr = isGraveyard
        ? nextLog?.timeAmOut && nextLog?.timeAmOut !== "--:--"
          ? nextLog?.timeAmOut
          : log.timeOut
        : log.timeOut && log.timeOut !== "--:--"
          ? log.timeOut
          : log.timeAmOut;


      const actualTimeInMins = parseTimeToMinutes(actualTimeInStr);
      const actualTimeOutMins = parseTimeToMinutes(actualTimeOutStr);


      let normalizedSchedInMins = schedInMins;
      let normalizedSchedOutMins = schedOutMins;
      // let normalizedActualTimeOutMins = actualTimeOutMins;

      // const actualTimeOutIsOnNextDay =
      //   nextLog && actualTimeOutStr === nextLog.timeAmOut && actualTimeOutStr !== "--:--";
      // const graveyardCrossesMidnight =
      //   isGraveyard &&
      //   schedInMins != null &&
      //   schedOutMins != null &&
      //   schedInMins > schedOutMins;

      // if (graveyardCrossesMidnight && actualTimeOutMins != null) {
      //   normalizedSchedOutMins = schedOutMins + 1440;
      //   if (
      //     actualTimeOutMins > 0 &&
      //     (actualTimeOutIsOnNextDay || actualTimeOutMins < schedInMins)
      //   ) {
      //     normalizedActualTimeOutMins = actualTimeOutMins + 1440;
      //   }
      // }

      if (isGraveyard && schedOutMins != null && actualTimeOutMins != null) {
        // For graveyard, schedule timeout is typically on next day (e.g., 6 AM = 360)
        // Check if actual timeout is from same day (log.timeOut) vs next day (nextLog.timeAmOut)
        const actualTimeOutIsOnNextDay = nextLog && actualTimeOutStr === nextLog.timeAmOut && actualTimeOutStr !== "--:--";
        
        if (!actualTimeOutIsOnNextDay) {
          // Actual timeout is on same day, but schedule timeout is on next day
          // Add 24 hours (1440 minutes) to schedule timeout for comparison
          // Only do this if schedule timeout is less than actual (indicating schedule is on next day)
          if (schedOutMins < actualTimeOutMins) {
            normalizedSchedOutMins = schedOutMins + 1440;
          }
        }
      }

      const rawLate =
        normalizedSchedInMins != null && actualTimeInMins != null && actualTimeInMins > 0
          ? Math.max(0, actualTimeInMins - normalizedSchedInMins)
          : 0;
      // const rawUndertime =
      //   normalizedSchedOutMins != null &&
      //   normalizedActualTimeOutMins != null &&
      //   normalizedActualTimeOutMins > 0
      //     ? Math.max(0, normalizedSchedOutMins - normalizedActualTimeOutMins)
      //     : 0;

      const rawUndertime =
      normalizedSchedOutMins != null && actualTimeOutMins != null && actualTimeOutMins > 0
        ? Math.max(0, normalizedSchedOutMins - actualTimeOutMins)
        : 0;

          // console.log("rawLate", rawLate);

      // For graveyard shifts, time out might be on next day, so pass nextLog.date if available
      const nextLogDate = isGraveyard && nextLog?.date ? nextLog.date : null;
      
      // For 24/7 employees, suspensions should not affect late/undertime calculations
      const overlapIn = !isIndividualPayroll && !isTwentyFourSeven && rawLate > 0 && schedInMins != null && actualTimeInMins != null
        ? getSuspensionOverlapMins(suspensionRangesByDate, log.date, schedInMins, actualTimeInMins)
        : 0;
      
      // For undertime: if graveyard and using nextLog, check suspensions on both current and next day
      // Skip for 24/7 employees
      const overlapOut = !isIndividualPayroll && !isTwentyFourSeven && rawUndertime > 0 && schedOutMins != null && actualTimeOutMins != null
        ? getSuspensionOverlapMins(suspensionRangesByDate, log.date, actualTimeOutMins, schedOutMins, nextLogDate)
        : 0;

      const late = Math.max(rawLate - overlapIn, 0);
      const undertime = Math.max(rawUndertime - overlapOut, 0);

      // console.log("late", late);
      // console.log("undertime", undertime);
      // console.log("log_", log);
      
      const totalLateUTMins = (late ?? 0) + (undertime ?? 0);
      // console.log("LOG_000", log);

      // console.log("late", late);
      // console.log("undertime", undertime);

      const isAbsent = _isAbsent(log, isGraveyard, absentRules, totalLateUTMins, late, undertime, nextLog);
      const isHalfDay = _isHalfDay(log, isGraveyard, payrollWithHalfDay, late, undertime, nextLog);

      // console.log("isAbsent", isAbsent);
      // console.log("isHalfDays", isHalfDay);

      const minutesToDeduct = absentRules.isAP 
        ? getAPBracketedMinutes(totalLateUTMins)
        : totalLateUTMins;

      
        if (!employeeLateUTMins[log.userID]) {
          employeeLateUTMins[log.userID] = {
            regular: createEmployeeStats(dateRangeCount, isArrayDate),
            airport: createEmployeeStats(dateRangeCount, isArrayDate)
          };
        }

        const isAirport = isAugmentationPayroll ? isAirportDate(log.userID, log.date) : false;
        const statsKey = isAirport ? 'airport' : 'regular';

      // Only add late/undertime minutes if NOT absent and NOT halfday
      const shouldAddMinutes = !isAbsent && !isHalfDay;

      if (isArrayDate) {
        const dateRangeIndex = getDateRangeIndex(log.date, parsedDateRanges);
        if (dateRangeIndex < 0) continue;

        if (shouldAddMinutes) {
          employeeLateUTMins[log.userID][statsKey].totalLateUTMins[dateRangeIndex] += minutesToDeduct;

          if (late > 0 && isMonday(log.date) && isLateAfter7AM(actualTimeInMins, isGraveyard, schedInMins)) {
            employeeLateUTMins[log.userID][statsKey].lateCount[dateRangeIndex] += 1;
            if (log.date) {
              employeeLateUTMins[log.userID][statsKey].lateDates[dateRangeIndex].add(log.date);
            }
          }

          employeeLateUTMins[log.userID][statsKey].minutesIncurred[dateRangeIndex].push({totalLateUTMins: minutesToDeduct, twentyFourSeven});
        }

        if (isAbsent) {
          employeeLateUTMins[log.userID][statsKey].absentCount[dateRangeIndex] += 1;
          if (log.date) {
            employeeLateUTMins[log.userID][statsKey].absentDates[dateRangeIndex].add(log.date);
          }
        } else if (isHalfDay) {
          employeeLateUTMins[log.userID][statsKey].halfDayCount[dateRangeIndex] += 1;
          if (log.date) {
            employeeLateUTMins[log.userID][statsKey].halfDayDates[dateRangeIndex].add(log.date);
          }
        }
      } else {
        if (shouldAddMinutes) {
          employeeLateUTMins[log.userID][statsKey].totalLateUTMins += minutesToDeduct;

          if (late > 0 && isMonday(log.date) && isLateAfter7AM(actualTimeInMins, isGraveyard, schedInMins)) {
            employeeLateUTMins[log.userID][statsKey].lateCount += 1;
            if (log.date) {
              employeeLateUTMins[log.userID][statsKey].lateDates.add(log.date);
            }
          }
          employeeLateUTMins[log.userID][statsKey].minutesIncurred.push({totalLateUTMins: minutesToDeduct, twentyFourSeven});
        }

        if (isAbsent) {
          employeeLateUTMins[log.userID][statsKey].absentCount += 1;
          if (log.date) {
            employeeLateUTMins[log.userID][statsKey].absentDates.add(log.date);
          }
        } else if (isHalfDay) {
          employeeLateUTMins[log.userID][statsKey].halfDayCount += 1;
          if (log.date) {
            employeeLateUTMins[log.userID][statsKey].halfDayDates.add(log.date);
          }
        }
      }
      
    }


    for (const userID in lookupData) {
      const workingDaysSet = lookupData[userID]?.workingDaysSet;
      if (!workingDaysSet || !(workingDaysSet instanceof Set)) continue;

      const logDates = logDatesByUser[userID] || new Set();
      const nonWorkingOverrideDates = nonWorkingOverrideDatesByUser[userID] || new Set();

      const holidaySet = lookupData[userID]?.holidays;
      const suspensionSet = lookupData[userID]?.suspensions;
      const manualDtrSet = lookupData[userID]?.manualDtr;
      const obSet = lookupData[userID]?.ob;
      const timeoffSet = lookupData[userID]?.timeoff;
      const leaveWithPaySet = lookupData[userID]?.leaveWithPay;
      const leaveWithoutPaySet = lookupData[userID]?.leaveWithoutPay;
      const mandatoryLeaveSet = lookupData[userID]?.mandatoryLeave;
      const isTwentyFourSeven = lookupData[userID]?.isTwentyFourSeven || false;

      if (logDates.size >= workingDaysSet.size) {
        let allDaysHaveLogs = true;
        for (const day of workingDaysSet) {
          if (!logDates.has(day)) {
            allDaysHaveLogs = false;
            break; 
          }
        }
        if (allDaysHaveLogs) continue; 
      }



     
      if (!employeeLateUTMins[userID]) {
        employeeLateUTMins[userID] = {
          regular: createEmployeeStats(dateRangeCount, isArrayDate),
          airport: createEmployeeStats(dateRangeCount, isArrayDate)
        };
      }

      for (const workingDay of workingDaysSet) {
        if (!isDateInTaggingRanges(workingDay, parsedDateRanges)) {
          continue;
        }

        // Skip holidays/suspensions (non-24/7 only), manual DTR, OB, or timeoff (all employees) when checking for absences
        if ((!isIndividualPayroll && shouldSkipDate(workingDay, { dateSet: holidaySet, isTwentyFourSeven, check24Seven: true })) ||
            (!isIndividualPayroll && shouldSkipDate(workingDay, { dateSet: suspensionSet, isTwentyFourSeven, check24Seven: true })) ||
            shouldSkipDate(workingDay, { dateSet: leaveWithoutPaySet }) ||
            shouldSkipDate(workingDay, { dateSet: manualDtrSet }) ||
            shouldSkipDate(workingDay, { dateSet: obSet }) ||
            shouldSkipDate(workingDay, { dateSet: timeoffSet })) {
          continue; 
        }

        const skipLeave = absentRules.isAP
        ? mandatoryLeaveSet?.has(workingDay) ?? false
        : leaveWithPaySet?.has(workingDay) ?? false;
      
        if (skipLeave) {
          continue;
        }

        if (nonWorkingOverrideDates.has(workingDay)) {
          continue;
        }

        const isAirport = isAugmentationPayroll ? isAirportDate(userID, workingDay) : false;
        const statsKey = isAirport ? 'airport' : 'regular';

        if (isArrayDate) {
          const dateRangeIndex = getDateRangeIndex(workingDay, parsedDateRanges);
          if (dateRangeIndex < 0) continue;

          const existingAbsentDates = employeeLateUTMins[userID][statsKey].absentDates[dateRangeIndex];

          if (!logDates.has(workingDay) && !existingAbsentDates.has(workingDay)) {
            existingAbsentDates.add(workingDay); 
            employeeLateUTMins[userID][statsKey].absentCount[dateRangeIndex] += 1;
          }
        } else {
          const existingAbsentDates = employeeLateUTMins[userID][statsKey].absentDates;

          // console.log("existingAbsentDates", existingAbsentDates);
          if (!logDates.has(workingDay) && !existingAbsentDates.has(workingDay)) {
            existingAbsentDates.add(workingDay); 
            employeeLateUTMins[userID][statsKey].absentCount += 1;
          }
        }
      }
    }
    
    return employeeLateUTMins;
  }
